import { ITEMS_PER_PAGE } from '@/constants';
import i18n from '@/i18n';
import { default as apiService } from '@/services/api.ts';
import { useAuthStore } from '@/stores/auth';
import { useSortingStore } from '@/stores/sorting';
import { useToastStore } from '@/stores/toast';
import type { TimeEntry, TimeEntryFilter } from '@/types';
import { log } from '@/utils/log';
import { readPageSize, storePageSize } from '@/utils/pageSize';
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

const storeId = 'timeEntries';
export const useTimeEntriesStore = defineStore(storeId, () => {
    const authStore = useAuthStore();
    const toast = useToastStore();
    const sortStore = useSortingStore(storeId);

    const models = ref<TimeEntry[]>([]);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const totalItems = ref(0);
    const itemsPerPage = ref(ITEMS_PER_PAGE);
    const isLoading = ref(false);
    let latestListRequest = 0;

    const filter = ref<TimeEntryFilter>({
        customer_id: null,
        project_id: null,
        date_from: null,
        date_to: null,
        billable: null,
        billed: null,
    });

    watch(
        () => authStore.activeCompany?.id,
        async (newId, oldId) => {
            if (newId !== oldId && newId) {
                await loadPage(1);
            }
        },
    );

    async function loadPage(page: number) {
        const requestId = ++latestListRequest;
        const requestedFilter = { ...filter.value };
        isLoading.value = true;
        try {
            const activeCompanyId = authStore.activeCompany?.id;
            const params = activeCompanyId ? { ...requestedFilter, company_id: activeCompanyId } : requestedFilter;

            const response = await apiService.timeEntry.index(
                page,
                params,
                sortStore.currentSort,
                readPageSize(storeId),
            );
            if (requestId !== latestListRequest || JSON.stringify(requestedFilter) !== JSON.stringify(filter.value)) {
                return;
            }
            models.value = response.data.data;
            currentPage.value = response.data.meta.current_page;
            totalPages.value = response.data.meta.last_page;
            totalItems.value = response.data.meta.total;
            itemsPerPage.value = response.data.meta.per_page;
        } catch (error) {
            if (requestId === latestListRequest) {
                log('Failed to load time entries:', error);
            }
        } finally {
            if (requestId === latestListRequest) {
                isLoading.value = false;
            }
        }
    }

    async function applyFilter() {
        await loadPage(1);
    }

    async function resetFilter() {
        filter.value = {
            customer_id: null,
            project_id: null,
            date_from: null,
            date_to: null,
            billable: null,
            billed: null,
        };
        await loadPage(1);
    }

    function isFiltered(): boolean {
        return Object.values(filter.value).some((v) => v !== null && v !== undefined);
    }

    async function deleteEntry(id: string) {
        try {
            await apiService.timeEntry.delete(id);
            toast.success(i18n.global.t('timeEntries.entryDeleted'));
        } catch (error) {
            log('Failed to delete time entry:', error);
            toast.error(i18n.global.t('error.generic'));
            throw error;
        }
    }

    /** Switches this list to another page size, remembers it and reloads from page 1. */
    async function setItemsPerPage(size: number) {
        if (!storePageSize(storeId, size)) return;
        itemsPerPage.value = size;
        await loadPage(1);
    }

    return {
        models,
        filter,
        sortStore,
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        setItemsPerPage,
        isLoading,
        loadPage,
        applyFilter,
        resetFilter,
        isFiltered,
        deleteEntry,
    };
});
